import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Image as ImageIcon, X, Upload } from "lucide-react";

const CATEGORIES = ["track", "roller", "handle", "lock", "spring", "mesh", "frame", "hardware", "other"];
const EMPTY_FORM = {
  id: null,
  name: "",
  sku: "",
  category: "track",
  description: "",
  price: 0,
  admin_cost: 0,
  image_url: "",
  in_stock: true,
  stock_count: "",
  active: true,
};

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

export default function AdminCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form state when modal open
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/catalog");
      setItems(r.data.items || []);
    } catch { toast.error("Failed to load catalog"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY_FORM });
  const openEdit = (it) => setEditing({
    ...EMPTY_FORM, ...it,
    stock_count: it.stock_count == null ? "" : it.stock_count,
  });

  const save = async () => {
    if (!editing.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        sku: editing.sku || null,
        category: editing.category,
        description: editing.description,
        price: Number(editing.price) || 0,
        admin_cost: Number(editing.admin_cost) || 0,
        image_url: editing.image_url || null,
        in_stock: !!editing.in_stock,
        stock_count: editing.stock_count === "" ? null : Number(editing.stock_count),
        active: !!editing.active,
      };
      if (editing.id) {
        await api.patch(`/admin/catalog/${editing.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/admin/catalog", payload);
        toast.success("Item created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/catalog/${it.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/catalog/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((e) => ({ ...e, image_url: r.data.image_url }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold text-[var(--brand-text)]">Catalog</h1>
        <button data-testid="catalog-new-btn" onClick={openNew}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold brand-bg text-white">
          <Plus className="w-3.5 h-3.5" /> New item
        </button>
      </div>

      {loading ? <p className="muted-text">Loading…</p> : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[var(--brand-border)]">
          <p className="muted-text">No catalog items yet. Click <strong>New item</strong> to get started.</p>
        </div>
      ) : (
        <>
          {/* Mobile: card layout (everything visible, big Edit button) */}
          <div className="md:hidden space-y-3">
            {items.map((it) => {
              const profit = Number(it.price || 0) - Number(it.admin_cost || 0);
              const margin = it.price ? (profit / Number(it.price)) * 100 : 0;
              const marginColor = margin >= 30 ? "var(--brand-green)" : margin >= 0 ? "#854d0e" : "#dc2626";
              return (
                <div key={it.id} data-testid={`catalog-row-${it.slug}`}
                  className="bg-white rounded-2xl border border-[var(--brand-border)] p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded-lg bg-[#f5f7f6] flex items-center justify-center overflow-hidden shrink-0">
                      {it.image_url
                        ? <img src={it.image_url} alt={it.name} className="w-full h-full object-contain" />
                        : <ImageIcon className="w-5 h-5 muted-text" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[var(--brand-text)] leading-tight">{it.name}</p>
                      <p className="text-[11px] muted-text">{it.sku || it.slug} · <span className="capitalize">{it.category}</span></p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${it.active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>
                          {it.active ? "Live" : "Off"}
                        </span>
                        {it.stock_count != null && it.stock_count <= 2 && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            Low · {it.stock_count} left
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <div>
                      <p className="muted-text uppercase tracking-wider text-[10px]">Price</p>
                      <p className="font-bold text-[var(--brand-text)] tabular-nums">{money(it.price)}</p>
                    </div>
                    <div>
                      <p className="muted-text uppercase tracking-wider text-[10px]">Cost</p>
                      <p className="font-bold muted-text tabular-nums">{money(it.admin_cost)}</p>
                    </div>
                    <div>
                      <p className="muted-text uppercase tracking-wider text-[10px]">Margin</p>
                      <p className="font-bold tabular-nums" style={{ color: marginColor }}>{margin.toFixed(0)}%</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`catalog-edit-${it.slug}`} onClick={() => openEdit(it)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-semibold border-2 border-[var(--brand-green)] brand-text">
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button data-testid={`catalog-delete-${it.slug}`} onClick={() => remove(it)}
                      aria-label={`Delete ${it.name}`}
                      className="inline-flex items-center justify-center w-10 h-10 rounded-full border-2 border-red-200 text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: full table */}
          <div className="hidden md:block bg-white rounded-2xl border border-[var(--brand-border)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f5f7f6] text-left">
                <tr className="text-[11px] uppercase tracking-wider muted-text">
                  <th className="p-3">Item</th>
                  <th className="p-3">Cat</th>
                  <th className="p-3 text-right">Price</th>
                  <th className="p-3 text-right">Cost</th>
                  <th className="p-3 text-right">Margin</th>
                  <th className="p-3 text-right">Stock</th>
                  <th className="p-3">Active</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const profit = Number(it.price || 0) - Number(it.admin_cost || 0);
                  const margin = it.price ? (profit / Number(it.price)) * 100 : 0;
                  return (
                    <tr key={it.id} data-testid={`catalog-row-${it.slug}`} className="border-t border-[var(--brand-border)]">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#f5f7f6] flex items-center justify-center overflow-hidden">
                            {it.image_url
                              ? <img src={it.image_url} alt={it.name} className="w-full h-full object-contain" />
                              : <ImageIcon className="w-4 h-4 muted-text" />}
                          </div>
                          <div>
                            <p className="font-semibold text-[var(--brand-text)]">{it.name}</p>
                            <p className="text-[11px] muted-text">{it.sku || it.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-xs muted-text capitalize">{it.category}</td>
                      <td className="p-3 text-right tabular-nums">{money(it.price)}</td>
                      <td className="p-3 text-right tabular-nums muted-text">{money(it.admin_cost)}</td>
                      <td className="p-3 text-right tabular-nums" style={{ color: margin >= 30 ? "var(--brand-green)" : margin >= 0 ? "#854d0e" : "#dc2626" }}>
                        {margin.toFixed(0)}%
                      </td>
                      <td className="p-3 text-right tabular-nums">
                        {it.stock_count == null ? <span className="muted-text">—</span> : it.stock_count}
                        {it.stock_count != null && it.stock_count <= 2 && (
                          <span className="ml-1 text-[10px] font-bold uppercase text-amber-700">low</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${it.active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>
                          {it.active ? "Live" : "Off"}
                        </span>
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <div className="inline-flex gap-1.5">
                          <button data-testid={`catalog-edit-${it.slug}`} onClick={() => openEdit(it)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border-2 border-[var(--brand-border)] hover:border-[var(--brand-green)] hover:brand-text">
                            <Edit2 className="w-3 h-3" /> Edit
                          </button>
                          <button data-testid={`catalog-delete-${it.slug}`} onClick={() => remove(it)}
                            aria-label={`Delete ${it.name}`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[var(--brand-border)] sticky top-0 bg-white">
              <h2 className="font-bold text-[var(--brand-text)]">{editing.id ? "Edit item" : "New item"}</h2>
              <button onClick={() => setEditing(null)} className="p-1"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase muted-text">Image</label>
                <div className="mt-1 flex items-center gap-3">
                  <div className="w-24 h-24 rounded-xl bg-[#f5f7f6] flex items-center justify-center overflow-hidden">
                    {editing.image_url
                      ? <img src={editing.image_url} alt="" className="w-full h-full object-contain p-2" />
                      : <ImageIcon className="w-6 h-6 muted-text" />}
                  </div>
                  <div className="flex-1">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadImage(e.target.files?.[0])} />
                    <button data-testid="catalog-upload-image"
                      onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border-2 border-[var(--brand-border)] hover:border-[var(--brand-green)]">
                      <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload image"}
                    </button>
                    {editing.image_url && (
                      <button onClick={() => setEditing((e) => ({ ...e, image_url: "" }))}
                        className="ml-2 text-xs text-red-600 underline">Remove</button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Name" testid="catalog-name" value={editing.name}
                  onChange={(v) => setEditing((e) => ({ ...e, name: v }))} />
                <Field label="SKU (optional)" testid="catalog-sku" value={editing.sku || ""}
                  onChange={(v) => setEditing((e) => ({ ...e, sku: v }))} />
                <div>
                  <label className="text-xs font-bold uppercase muted-text">Category</label>
                  <select data-testid="catalog-category" value={editing.category}
                    onChange={(e) => setEditing((s) => ({ ...s, category: e.target.value }))}
                    className="rounded-input mt-1 capitalize">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase muted-text">Description</label>
                <textarea data-testid="catalog-description" value={editing.description}
                  onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))}
                  rows={3} className="w-full rounded-2xl border-2 border-[var(--brand-border)] bg-white px-3 py-2 text-sm mt-1" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <NumField label="Price ($)" testid="catalog-price" value={editing.price}
                  onChange={(v) => setEditing((e) => ({ ...e, price: v }))} />
                <NumField label="Hard cost ($) — admin only" testid="catalog-cost"
                  value={editing.admin_cost}
                  onChange={(v) => setEditing((e) => ({ ...e, admin_cost: v }))} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase muted-text">Stock count</label>
                  <input data-testid="catalog-stock-count" type="number" min="0"
                    placeholder="leave blank if untracked"
                    value={editing.stock_count}
                    onChange={(e) => setEditing((s) => ({ ...s, stock_count: e.target.value }))}
                    className="rounded-input mt-1" />
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <label className="flex items-center gap-2 text-sm">
                    <input data-testid="catalog-instock" type="checkbox" checked={!!editing.in_stock}
                      onChange={(e) => setEditing((s) => ({ ...s, in_stock: e.target.checked }))} />
                    In stock
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input data-testid="catalog-active" type="checkbox" checked={!!editing.active}
                      onChange={(e) => setEditing((s) => ({ ...s, active: e.target.checked }))} />
                    Active (visible on shop)
                  </label>
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-[var(--brand-border)] sticky bottom-0 bg-white flex gap-3">
              <button onClick={() => setEditing(null)}
                className="flex-1 py-2 rounded-full border-2 border-[var(--brand-border)] font-semibold">Cancel</button>
              <button data-testid="catalog-save-btn" onClick={save} disabled={saving}
                className="flex-1 py-2 rounded-full font-semibold brand-bg text-white disabled:opacity-50">
                {saving ? "Saving…" : "Save item"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase muted-text">{label}</label>
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="rounded-input mt-1" />
    </div>
  );
}

function NumField({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase muted-text">{label}</label>
      <input data-testid={testid} type="number" min="0" step="0.01" value={value || 0}
        onChange={(e) => onChange(e.target.value)} className="rounded-input mt-1" />
    </div>
  );
}
